import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  SectionHeading,
  tone,
  useOpenAgentDemo,
} from '@/components/agents/primitives';

import {
  formatMoney,
  PHONE_DOCS_URL,
  type PlanPrice,
  TWILIO_PER_MINUTE,
  TWILIO_US,
  useFormatLocale,
  usePlanPrice,
  useReceptionistT,
} from './kit';

type Faq = { question: string; answer: string };

/** Questions buyers ask before they hand over their phone line. */
export default function ReceptionistFaq({ price }: { price: PlanPrice }) {
  const { t } = useReceptionistT();
  const locale = useFormatLocale();
  const openDemo = useOpenAgentDemo();
  const plan = usePlanPrice(price);
  const number = formatMoney(TWILIO_US.localNumberPerMonth, 'usd', locale, 2);
  const perMinute = formatMoney(TWILIO_PER_MINUTE, 'usd', locale, 2);

  const faqs: Faq[] = [
    {
      question: t('How much does an AI receptionist cost?'),
      answer: plan
        ? t(
            'You need a paid Easy-Peasy.AI plan, from {{price}} a month on Starter, plus Twilio’s charges in your own Twilio account. In the US that’s about {{number}} a month for a local number and about {{perMinute}} a minute for calls. Building and testing your agent is free.',
            { price: plan, number, perMinute },
          )
        : t(
            'You need a paid Easy-Peasy.AI plan, plus Twilio’s charges in your own Twilio account. In the US that’s about {{number}} a month for a local number and about {{perMinute}} a minute for calls. Building and testing your agent is free.',
            { number, perMinute },
          ),
    },
    {
      question: t('Is there a free AI receptionist?'),
      answer: t(
        'You can build your agent, train it on your business and test its answers for free, and talk to it out loud in the Playground for up to 10 minutes every 30 days. Answering real phone calls needs a paid plan.',
      ),
    },
    {
      question: t('Do I need a Twilio account?'),
      answer: t(
        'Yes. Calls come in through a phone number in your own Twilio account, and Twilio bills the number and the minutes directly. You can buy a number there in a few minutes or use one you already have. To keep your current business number, forward its calls to the Twilio number or move the number to Twilio.',
      ),
    },
    {
      question: t('Can it transfer a call to me?'),
      answer: t(
        'Not yet. It answers questions, books appointments and takes messages. When a caller needs a person, have it take their name, number and reason and send them to you by email, so you can call back.',
      ),
    },
    {
      question: t('Which languages can it speak?'),
      answer: t(
        'Each phone number has one language, set with a code such as en-US, en-GB, es-ES, fr-FR, de-DE or ja-JP. Twilio offers more than 30 languages and accents for these calls, so check that yours is on its list before you connect a number.',
      ),
    },
    {
      question: t('Does it sound like a real person?'),
      answer: t(
        'It speaks in a natural voice, keeps its answers short and lets callers interrupt it. You write the greeting yourself, and many businesses use it to tell callers they’re talking to an AI assistant.',
      ),
    },
    {
      question: t('What happens when it doesn’t know the answer?'),
      answer: t(
        'It answers from your knowledge and instructions, and it’s told not to make things up. Tell it what to do when it isn’t sure, such as taking a message for your team.',
      ),
    },
    {
      question: t('Can it book appointments?'),
      answer: t(
        'Yes. Add the Google Calendar action and it checks your free times during the call and books the slot the caller picks into your calendar. It asks for the caller’s name and email to make the booking. Actions are included in every paid plan.',
      ),
    },
    {
      question: t('Is it the same agent as my website chat?'),
      answer: t(
        'Yes. One agent answers on your website, WhatsApp, email and the phone, with the same knowledge and instructions. On calls it switches to a short, spoken style.',
      ),
    },
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title={t('Questions, answered.')}
            lead={t(
              'Still wondering about something? Ask the agent on this page. It runs on the same platform your receptionist would.',
            )}
          />
          <div className='mt-8 flex flex-wrap items-center gap-x-6 gap-y-4'>
            <button
              type='button'
              onClick={openDemo}
              className={buttonSecondary}
            >
              <span className='relative flex h-2 w-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60 motion-reduce:animate-none' />
                <span className='relative inline-flex h-2 w-2 rounded-full bg-emerald-500' />
              </span>
              {t('Talk to our agent')}
            </button>
            <a
              href={PHONE_DOCS_URL}
              className='group inline-flex items-center gap-2 text-[15px] font-semibold text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
            >
              {t('Read the setup guide')}
              <ArrowGlyph className='transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
            </a>
          </div>
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }) => (
              <li key={question}>
                <details className='group'>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    <h3 className='text-lg font-semibold'>{question}</h3>
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
